import { useEffect, useRef, type CSSProperties } from 'react';
import { config } from '../config';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let loading: Promise<void> | null = null;

/** Google's ad script, loaded once, the first time the ad bubble shows. */
function loadAdSense(client: string): Promise<void> {
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('AdSense did not load'));
    document.head.append(script);
  });
  return loading;
}

/** Whether the board shows an ad (both AdSense settings are in the build). */
export const adsEnabled = () => !!config.adsense.client && !!config.adsense.slot;

/**
 * The one ad on the board: a Google AdSense unit in a whisper-like bubble marked "Advertisement",
 * in a fixed place in the whisper ring (whispers keep out of it). Unlike the whispers it never
 * moves, appears or disappears by itself: AdSense doesn't allow ads that reload on their own.
 */
export function AdBubble({ style }: { style: CSSProperties }) {
  const ref = useRef<HTMLModElement>(null);
  useEffect(() => {
    let cancelled = false;
    loadAdSense(config.adsense.client)
      .then(() => {
        if (cancelled || !ref.current || ref.current.dataset.adsbygoogleStatus) return;
        (window.adsbygoogle ??= []).push({});
      })
      .catch(() => undefined); // blocked by the reader's browser: the bubble stays empty
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <aside className="ad-bubble" style={style} aria-label="Advertisement">
      <span className="ad-bubble__label">Advertisement</span>
      <ins
        ref={ref}
        className="adsbygoogle ad-bubble__unit"
        data-ad-client={config.adsense.client}
        data-ad-slot={config.adsense.slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}
